// ** Redux Imports
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"

// ** API
import API from "api/special_orders"
import DocumentsAPI from "api/documents"

// ---------------------------------------------
// LIST SPECIAL ORDERS
// ---------------------------------------------
export const getData = createAsyncThunk(
  "specialOrders/getData",
  async (params, { signal }) => {
    const response = await API.listSpecialOrders(params)
    if (signal.aborted) return

    return {
      category: params.category,
      params,
      data: response.data || [],
      totalRows: response.totalRows || 0
    }
  }
)

// ---------------------------------------------
// GET ONE (VIEW PAGE)
// ---------------------------------------------
export const getSpecialOrder = createAsyncThunk(
  "specialOrders/getOne",
  async (name, { dispatch, getState }) => {
    const loaded = getState().specialOrders.data?.filter(e => e.name === name)[0]
    if (loaded) dispatch(specialOrdersSlice.actions.setSelected(loaded))
    return await API.getSpecialOrder(name)
  }
)

// ---------------------------------------------
// CREATE SPECIAL ORDER
// ---------------------------------------------
export const createSpecialOrder = createAsyncThunk(
  "specialOrders/create",
  async (order, { dispatch, getState }) => {
    dispatch(specialOrdersSlice.actions.setUpdating(true))

    const created = await API.createSpecialOrder(order)

    dispatch(specialOrdersSlice.actions.setUpdating(false))

    // Reload list with same filters
    // await dispatch(getData(getState().specialOrders.params))

    const params = getState().specialOrders.params 
    if (params) {
      await dispatch(getData(params))
    }

    return created
  }
)

// ---------------------------------------------
// UPDATE ORDER
// ---------------------------------------------
export const updateSpecialOrder = createAsyncThunk(
  "specialOrders/update",
  async (order, { dispatch, getState }) => {
    dispatch(specialOrdersSlice.actions.setUpdating(true))

    const updated = await API.updateSpecialOrder(order)

    dispatch(specialOrdersSlice.actions.setUpdating(false))
    // Refresh selected
    dispatch(specialOrdersSlice.actions.setSelected(updated))

    // Refresh list with same filters
    // await dispatch(getData(getState().specialOrders.params))

    const params = getState().specialOrders.params
    if (params?.category) {
      await dispatch(getData(params))
    }

    return updated
  }
)

// ---------------------------------------------
// ADD PAYMENT
// ---------------------------------------------
export const addSpecialOrderPayment = createAsyncThunk(
  'specialOrders/addPayment',
  async ({ payment }, { dispatch, rejectWithValue }) => {

    // TEMP ID for UI
    const optimisticPayment = {
      ...payment,
      name: `temp-${Date.now()}`
    }

    // 👇 UI updates immediately
    dispatch(specialOrdersSlice.actions.addPaymentOptimistic(optimisticPayment))

    try {
      const created = await API.createPayment(payment)

      // Replace temp with real one
      await dispatch(getSpecialOrderPayments(payment.specialOrderName))
      await dispatch(getSpecialOrder(payment.specialOrderName))

      return created
    } catch (err) {
      // ❌ rollback
      dispatch(
        specialOrdersSlice.actions.deletePaymentOptimistic(
          optimisticPayment.name
        )
      )
      return rejectWithValue(err)
    }
  }
)

// ---------------------------------------------
// GET PAYMENTS
// ---------------------------------------------
export const getSpecialOrderPayments = createAsyncThunk(
  'specialOrders/getPayments',
  async (specialOrderName) => {
    return await API.listPayments(specialOrderName)
  }
)

// ---------------------------------------------
// UPDATE PAYMENT
// ---------------------------------------------
export const updateSpecialOrderPayment = createAsyncThunk(
  'specialOrders/updatePayment',
  async ({ payment }, { dispatch, getState, rejectWithValue }) => {

    const prev = getState().specialOrders.payments
      .find(p => p.name === payment.name)

    // optimistic update
    dispatch(specialOrdersSlice.actions.updatePaymentOptimistic(payment))

    try {
      await API.updatePayment({ payment })

      await dispatch(getSpecialOrderPayments(payment.specialOrderName))
      await dispatch(getSpecialOrder(payment.specialOrderName))
      
      return true
    } catch (err) {
      // rollback
      dispatch(
        specialOrdersSlice.actions.updatePaymentOptimistic(prev)
      )
      return rejectWithValue(err)
    }
  }
)

// ---------------------------------------------
// DELETE PAYMENT
// ---------------------------------------------
export const deleteSpecialOrderPayment = createAsyncThunk(
  'specialOrders/deletePayment',
  async ({ paymentName, specialOrderName }, { dispatch, getState, rejectWithValue }) => {

    const prev = getState().specialOrders.payments.find(p => p.name === paymentName)

    // instant remove
    dispatch(specialOrdersSlice.actions.deletePaymentOptimistic(paymentName))

    try {
      await API.deletePayment(paymentName)

      await dispatch(getSpecialOrderPayments(specialOrderName))
      await dispatch(getSpecialOrder(specialOrderName))

      return true
    } catch (err) {
      // rollback
      dispatch(
        specialOrdersSlice.actions.addPaymentOptimistic(prev)
      )
      return rejectWithValue(err)
    }
  }
)

export const getSpecialOrderDocuments = createAsyncThunk(
  "specialOrders/getDocuments",
  async (specialOrderName) => {
    const response = await DocumentsAPI.listDocuments({
      location: specialOrderName
    })
    return { [specialOrderName]: response }
  }
)

export const getSpecialOrderDocument = createAsyncThunk(
  "specialOrders/getDocument",
  async (name) => {
    return await DocumentsAPI.getDocument(name)
  }
)

export const addSpecialOrderDocument = createAsyncThunk(
  "specialOrders/addDocument",
  async (document) => {
    const updated = await DocumentsAPI.createDocument(document)
    return { ...document, ...updated }
  }
)

export const deleteSpecialOrderDocument = createAsyncThunk(
  "specialOrders/deleteDocument",
  async ({ name, specialOrderName }, { dispatch }) => {
    await DocumentsAPI.deleteDocument(name)
    await dispatch(getSpecialOrderDocuments(specialOrderName))
    return name
  }
)


// ---------------------------------------------
// SLICE
// ---------------------------------------------
export const specialOrdersSlice = createSlice({
   name: "specialOrders",
    initialState: {
    params: null,
      lists: {
        box: {
          data: [],
          totalRows: 0,
          loading: false
        },
        special_car: {
          data: [],
          totalRows: 0,
          loading: false
        }
      },

        updating: false,
        selectedOrder: null,
        payments: [],
        documents: {},
    },
  reducers: {
    setUpdating: (state, { payload }) => {
      state.updating = payload
    },
    setSelected: (state, { payload }) => {
      state.selectedOrder = payload
    },
    setPayment: (state, { payload }) => {
      state.payments = payload
    },
    addPaymentOptimistic: (state, { payload }) => {
      state.payments.unshift(payload)
    },
    updatePaymentOptimistic: (state, { payload }) => {
      state.payments = state.payments.map(p =>
      p.name === payload.name ? payload : p
     )
    },
    deletePaymentOptimistic: (state, { payload }) => {
      state.payments = state.payments.filter(p => p.name !== payload)
    }
  },
  extraReducers: builder => {
    builder

      // LIST PENDING
      .addCase(getData.pending, (state, action) => {
        const category = action.meta.arg.category

        // 🛡️ SAFETY GUARD
        if (!state.lists[category]) return

        state.lists[category].loading = true
      })

      // LIST FULFILLED
      .addCase(getData.fulfilled, (state, { payload }) => {
        const { category, data, totalRows, params } = payload

        // 🛡️ SAFETY GUARD
        if (!state.lists[category]) return

        state.params = params

        state.lists[category] = {
          data,
          totalRows,
          loading: false
        }
      })

      // LIST REJECTED
      .addCase(getData.rejected, (state, action) => {
        const category = action.meta.arg.category

        // 🛡️ SAFETY GUARD
        if (!state.lists[category]) return

        state.lists[category].loading = false
      })

      // VIEW
      .addCase(getSpecialOrder.pending, state => {
        state.orderLoading = true
      })
      .addCase(getSpecialOrder.fulfilled, (state, { payload }) => {
        state.orderLoading = false
        state.selectedOrder = payload
      })
      .addCase(getSpecialOrderPayments.fulfilled, (state, { payload }) => {
        state.payments = payload
      })
      .addCase(getSpecialOrderDocuments.fulfilled, (state,{payload}) => {
        state.documents = {
          ...state.documents,
          ...payload
        }
      }
    )
  }
})

export default specialOrdersSlice.reducer
